import {
	type CustomField,
	getFlagColor,
	RESERVATION_SYSTEM_FIELDS,
	ReservationStatusFilter,
	ResourceType,
} from '@cheqroom/api';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';

import type { FilterFieldEntry } from './types';
import { getDynamicFilterEntries } from './utils/get-dynamic-filter-entries';

const EMPTY_HIDE_FILTERS: string[] = [];

const getReservationStatusOptions = (t: TFunction<'reservation-info'>) => [
	{ label: t('reservation-info:status.draft'), value: ReservationStatusFilter.DRAFT },
	{ label: t('reservation-info:status.open'), value: ReservationStatusFilter.OPEN },
	{ label: t('reservation-info:status.overdue'), value: ReservationStatusFilter.OVERDUE },
	{ label: t('reservation-info:status.converted'), value: ReservationStatusFilter.CONVERTED },
	{ label: t('reservation-info:status.closed'), value: ReservationStatusFilter.CLOSED },
	{ label: t('reservation-info:status.cancelled'), value: ReservationStatusFilter.CANCELLED },
	{ label: t('reservation-info:status.archived'), value: ReservationStatusFilter.ARCHIVED },
];

const getMaintenanceOptions = (t: TFunction<'booking-filters'>, resourceType: ResourceType) => [
	{ label: t('booking-filters:maintenance.all'), value: 'all' },
	{ label: t('booking-filters:maintenance.include', { resourceType }), value: 'maintenance' },
	{ label: t('booking-filters:maintenance.exclude', { resourceType }), value: 'no_maintenance' },
];

const getConflictsOptions = (t: TFunction<'booking-filters'>) => [
	{ label: t('booking-filters:conflicts.all'), value: 'all' },
	{ label: t('booking-filters:conflicts.include'), value: 'with_conflicts' },
	{ label: t('booking-filters:conflicts.exclude'), value: 'no_conflicts' },
];

const getRepeatingOptions = (t: TFunction<'booking-filters'>) => [
	{ label: t('booking-filters:repeating.options.all'), value: 'all' },
	{ label: t('booking-filters:repeating.options.repeating'), value: 'repeating' },
	{ label: t('booking-filters:repeating.options.non_repeating'), value: 'non_repeating' },
];

type ReservationLabel = { id: string; name: string; color: string };

export const UNLABELED_RESERVATION_LABEL_ID = 'unlabeled';

const getReservationLabelOptions = (t: TFunction<'common'>, labels: ReservationLabel[]) => [
	{
		label: t('common:filter.labels.unlabeled'),
		value: UNLABELED_RESERVATION_LABEL_ID,
		color: getFlagColor('LIME_GREEN'),
		icon: 'RoundedSquare',
	},
	...labels.map((label) => ({
		label: label.name,
		value: label.id,
		color: getFlagColor(label.color),
		icon: 'RoundedSquare',
	})),
];

interface UseReservationFiltersProps {
	t: TFunction<'reservation-info' | 'booking-filters' | 'common'>;
	customFields?: CustomField[];
	reservationLabels?: ReservationLabel[];
	hideFilters?: string[];
}

export const useReservationFilters = ({
	t,
	customFields,
	reservationLabels,
	hideFilters = EMPTY_HIDE_FILTERS,
}: UseReservationFiltersProps) => {
	const filters = useMemo<FilterFieldEntry[]>(() => {
		const allStaticFilters: FilterFieldEntry[] = [
			{
				id: 'reservationStatus',
				name: t('filter.status.trigger'),
				type: 'select',
				options: getReservationStatusOptions(t),
			},
			{
				id: 'maintenance',
				name: t('booking-filters:maintenance.trigger'),
				type: 'select',
				selectMode: 'single',
				options: getMaintenanceOptions(t, ResourceType.RESERVATIONS),
			},
			{
				id: 'conflicts',
				name: t('booking-filters:conflicts.trigger'),
				type: 'select',
				selectMode: 'single',
				options: getConflictsOptions(t),
			},
			{ id: 'date_range', name: t('filter.date.trigger_dates'), type: 'dateRange' },
			{ id: 'locations', name: t('filter.location.trigger') },
			{
				id: 'labels',
				name: t('filter.labels.trigger'),
				type: 'select',
				options: getReservationLabelOptions(t, reservationLabels ?? []),
			},
			{ id: 'users', name: t('filter.user.trigger'), type: 'customer' },
			{
				id: 'repeating',
				name: t('booking-filters:repeating.trigger'),
				type: 'select',
				selectMode: 'single',
				options: getRepeatingOptions(t),
			},
		];
		const staticFilters = allStaticFilters.filter((entry) => !hideFilters.includes(entry.id));

		const dynamicFilters = getDynamicFilterEntries([
			...RESERVATION_SYSTEM_FIELDS,
			...(customFields ?? []).map((field) => ({ ...field, id: `fields.${field.name}` })),
		]);

		return [...staticFilters, ...dynamicFilters];
	}, [t, customFields, reservationLabels, hideFilters]);

	return {
		filters,
	};
};
